import React, { forwardRef, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Avatar from "./Avatar";
import {
    getNextHighlightedIndexOnArrowKey,
    useDropdownPosition,
    useOutsideClick,
    useScrollHighlightedIntoView,
} from "../../hooks/useDropdownBehavior";
import {
    StyledChevron,
    StyledClearButton,
    StyledDefaultInput,
    StyledDropdownItem,
    StyledDropdownList,
    StyledEnd,
    StyledInfoIcon,
    StyledInputGroup,
    StyledLabel,
    StyledMessage,
    StyledOptionContent,
    StyledOptionLabel,
    StyledRequired,
    StyledSelectWrapper,
    StyledStateItem,
    StyledTop,
} from "./SelectDropdown.styles";
import {
    StyledOptionAvatar,
    StyledSearchInput,
    StyledTriggerBox,
} from "./SearchableSelectDropdown.styles";

/**
 * Searchable single-select dropdown: a visible, typable text input (unlike `SelectDropdown`'s
 * typeahead against a read-only trigger), filtering `options` client-side as the person types.
 * @param {Array<{value, label, avatar?, disabled?}>} [props.options] Selectable options.
 * @param {(event: { target: { value, name }, selectedOption }) => void} [props.onChange] Also fired
 *   (with an empty value) by the clear button.
 * @param {React.Ref} ref Forwarded to the search input element.
 * Also: placeholder, value, size, disabled, disabledReason, loading, loadingMessage, emptyMessage/emptyText,
 *   error, label, required, info, helperText, errorText, clearable/isClearable, clearAriaLabel, name, id, ariaLabel, className (self-explanatory).
 */
const SearchableSelectDropdown = forwardRef(function SearchableSelectDropdown(
    {
        options = [],
        placeholder = "Search",
        value,
        onChange,
        onSelect,
        onClear,
        onOpenChange,
        size = "lg",
        disabled = false,
        disabledReason,
        loading = false,
        loadingMessage = "Loading options...",
        emptyMessage,
        emptyText,
        error = false,
        label,
        required = false,
        info,
        helperText,
        errorText,
        clearable = false,
        isClearable = false,
        clearAriaLabel = "Clear selection",
        name = "",
        id,
        ariaLabel,
        className,
        ...props
    },
    ref
) {
    const showClearButton = clearable || isClearable;

    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const containerRef = useRef(null);
    const listRef = useRef(null);
    const inputRef = useRef(null);

    const showError = Boolean(errorText || error);
    const selectedOption = useMemo(
        () => options.find((option) => String(option.value) === String(value)),
        [options, value]
    );
    const listboxId = id ? `${id}-listbox` : undefined;
    const message = errorText || helperText || (disabled ? disabledReason : undefined);
    const resolvedEmptyMessage = emptyMessage || emptyText || "No options available";

    const filteredOptions = useMemo(
        () => (query
            ? options.filter((option) => String(option.label || "").toLowerCase().includes(query.toLowerCase()))
            : options),
        [options, query]
    );
    const filteredOptionsLength = filteredOptions.length;
    /* Clamps the raw nav index (set by arrow keys/hover) against the current filtered list, without a state+effect round-trip. */
    const activeHighlightedIndex = highlightedIndex >= 0 && highlightedIndex < filteredOptionsLength ? highlightedIndex : -1;

    const setOpen = (nextOpen) => {
        setIsOpen(nextOpen);
        setHighlightedIndex(-1);

        if (!nextOpen) {
            setQuery("");
        }

        if (onOpenChange) {
            onOpenChange(nextOpen);
        }
    };

    const emitChange = (nextValue, option = null) => {
        const target = { value: nextValue, name };

        if (onChange) {
            onChange({ target, currentTarget: target, selectedOption: option });
        }
    };

    const handleSelect = (option) => {
        if (disabled || (option && option.disabled)) {
            return;
        }

        emitChange(option.value, option);

        if (onSelect) {
            onSelect(option);
        }

        setOpen(false);
    };

    const handleClear = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (disabled) {
            return;
        }

        emitChange("", null);

        if (onClear) {
            onClear();
        }

        setOpen(false);
    };

    const handleFocus = () => {
        if (!disabled) {
            setOpen(true);
        }
    };

    const handleQueryChange = (event) => {
        setQuery(event.target.value);
        setHighlightedIndex(-1);

        if (!isOpen) {
            setOpen(true);
        }
    };

    const handleKeyDown = (event) => {
        if (disabled) {
            return;
        }

        if (event.key === "Enter") {
            event.preventDefault();

            if (isOpen && activeHighlightedIndex >= 0 && filteredOptions[activeHighlightedIndex]) {
                handleSelect(filteredOptions[activeHighlightedIndex]);
            }

            return;
        }

        if (event.key === "Escape") {
            setOpen(false);

            if (inputRef.current) {
                inputRef.current.blur();
            }

            return;
        }

        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();

            if (!isOpen) {
                setOpen(true);
            }

            setHighlightedIndex((current) => getNextHighlightedIndexOnArrowKey(filteredOptions, current, event.key));
        }
    };

    const menuPosition = useDropdownPosition(containerRef, isOpen);

    useOutsideClick(containerRef, listRef, isOpen, () => setOpen(false));

    useScrollHighlightedIntoView(listRef, isOpen, activeHighlightedIndex);

    return (
        <StyledInputGroup className={className}>
            <StyledDefaultInput>
                {label && (
                    <StyledTop>
                        <StyledLabel htmlFor={id}>
                            {label}
                            {required && <StyledRequired aria-hidden="true">*</StyledRequired>}
                        </StyledLabel>

                        {info && <StyledInfoIcon className="fal fa-info-circle" title={info} aria-label={info} />}
                    </StyledTop>
                )}

                <StyledSelectWrapper ref={containerRef}>
                    <StyledTriggerBox
                        $size={size}
                        $disabled={disabled}
                        $error={showError}
                        title={disabled ? disabledReason : undefined}
                    >
                        {selectedOption?.avatar !== undefined && (
                            <StyledOptionAvatar aria-hidden="true">
                                <Avatar name={selectedOption?.label} image={selectedOption?.avatar} size={20} />
                            </StyledOptionAvatar>
                        )}

                        <StyledSearchInput
                            {...props}
                            ref={(node) => {
                                inputRef.current = node;
                                if (typeof ref === "function") {
                                    ref(node);
                                } else if (ref) {
                                    ref.current = node;
                                }
                            }}
                            id={id}
                            type="text"
                            role="combobox"
                            aria-label={ariaLabel || label || placeholder}
                            aria-haspopup="listbox"
                            aria-expanded={isOpen}
                            aria-controls={listboxId}
                            aria-disabled={disabled || undefined}
                            aria-invalid={showError || undefined}
                            placeholder={placeholder}
                            value={isOpen ? query : (selectedOption ? selectedOption.label : "")}
                            disabled={disabled}
                            onFocus={handleFocus}
                            onChange={handleQueryChange}
                            onKeyDown={handleKeyDown}
                        />

                        <StyledEnd>
                            {showClearButton && selectedOption && !disabled && (
                                <StyledClearButton type="button" onClick={handleClear} aria-label={clearAriaLabel}>
                                    <i className="fal fa-times" aria-hidden="true" />
                                </StyledClearButton>
                            )}

                            <StyledChevron $open={isOpen} className="fal fa-chevron-down" aria-hidden="true" />
                        </StyledEnd>
                    </StyledTriggerBox>

                    {isOpen && createPortal(
                        <StyledDropdownList
                            id={listboxId}
                            ref={listRef}
                            role="listbox"
                            $size={size}
                            $top={menuPosition.top}
                            $left={menuPosition.left}
                            $width={menuPosition.width}
                        >
                            {loading ? (
                                <StyledStateItem role="status">{loadingMessage}</StyledStateItem>
                            ) : filteredOptions.length === 0 ? (
                                <StyledStateItem>{resolvedEmptyMessage}</StyledStateItem>
                            ) : (
                                filteredOptions.map((option, index) => (
                                    <StyledDropdownItem
                                        key={option.value}
                                        role="option"
                                        aria-selected={String(option.value) === String(value)}
                                        aria-disabled={option.disabled || undefined}
                                        $selected={String(option.value) === String(value)}
                                        $highlighted={index === activeHighlightedIndex}
                                        $disabled={Boolean(option.disabled)}
                                        title={option.disabled ? option.disabledReason : option.title}
                                        onMouseDown={(event) => event.preventDefault()}
                                        onClick={() => handleSelect(option)}
                                        onMouseEnter={() => {
                                            if (!option.disabled) {
                                                setHighlightedIndex(index);
                                            }
                                        }}
                                    >
                                        <StyledOptionContent>
                                            {option.avatar !== undefined && (
                                                <StyledOptionAvatar aria-hidden="true">
                                                    <Avatar name={option.label} image={option.avatar} size={22} />
                                                </StyledOptionAvatar>
                                            )}

                                            <StyledOptionLabel>{option.label}</StyledOptionLabel>
                                        </StyledOptionContent>
                                    </StyledDropdownItem>
                                ))
                            )}
                        </StyledDropdownList>,
                        document.body
                    )}
                </StyledSelectWrapper>

                {message && <StyledMessage $error={Boolean(errorText)}>{message}</StyledMessage>}
            </StyledDefaultInput>
        </StyledInputGroup>
    );
});

SearchableSelectDropdown.displayName = "SearchableSelectDropdown";

export default SearchableSelectDropdown;
